import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Shield, AlertTriangle, CheckCircle2, Plus } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface ModelCard {
  id: string;
  modelName: string;
  provider: string;
  version: string;
  intendedUse: string;
  outOfScopeUses: string[];
  trainingData: string | null;
  evaluationData: string | null;
  metrics: Record<string, unknown>;
  biasAssessment: string | null;
  limitations: string | null;
  humanOversightNotes: string | null;
  riskLevel: 'MINIMAL' | 'LIMITED' | 'HIGH' | 'UNACCEPTABLE';
  approvedAt: string | null;
  approvedBy: string | null;
  createdAt: string;
}

// EU AI Act classification follows from the Praxara risk level — surfaced
// in the card so reviewers see how the platform maps to Annex III.
const EU_AI_ACT_BY_RISK: Record<ModelCard['riskLevel'], string> = {
  HIGH: 'Annex III high-risk',
  LIMITED: 'Annex III limited-risk',
  MINIMAL: 'Annex III minimal-risk',
  UNACCEPTABLE: 'Prohibited (Article 5)',
};

interface Risk {
  id: string;
  area: string;
  description: string;
  likelihood: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
  impact: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
  residualRisk: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
  mitigations: string;
  owner: string | null;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
  nextReviewAt: string | null;
}

export function AIGovernancePage() {
  const [tab, setTab] = useState<'models' | 'risks'>('models');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">AI governance</h1>
        <p className="text-sm text-muted-foreground">
          Model cards + risk register required under EU AI Act Articles 11 + 9 + 14. High-risk
          systems must maintain a technical file — these views keep yours audit-ready.
        </p>
      </div>

      <div className="flex gap-1 border-b">
        <Tab active={tab === 'models'} onClick={() => setTab('models')} label="Model cards" icon={Sparkles} />
        <Tab active={tab === 'risks'} onClick={() => setTab('risks')} label="Risk register" icon={Shield} />
      </div>

      {tab === 'models' ? <ModelCardsSection /> : <RiskRegisterSection />}
    </div>
  );
}

function Tab({
  active, onClick, label, icon: Icon,
}: {
  active: boolean; onClick: () => void; label: string; icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        '-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function ModelCardsSection() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const models = useQuery({
    queryKey: ['ai-gov', 'model-cards'],
    queryFn: async () => (await api.get('/api/ai-governance/model-cards')).data,
  });

  const approve = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/ai-governance/model-cards/${id}/approve`, {})).data,
    onSuccess: () => {
      toast.success('Model card approved');
      qc.invalidateQueries({ queryKey: ['ai-gov', 'model-cards'] });
    },
    onError: (err) => toast.error('Approve failed', extractApiError(err).message),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> New model card
        </Button>
      </div>

      {models.isLoading ? (
        <Skeleton className="h-64" />
      ) : !models.data?.items?.length ? (
        <EmptyState
          icon={Sparkles}
          title="No model cards yet"
          description="Register each AI model + version in use with its intended purpose, evaluation results, and risk class."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {(models.data.items as ModelCard[]).map((m) => (
            <Card key={m.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{m.modelName}</CardTitle>
                    <CardDescription>
                      {m.provider} · v{m.version}
                    </CardDescription>
                  </div>
                  <Badge variant={m.approvedAt ? 'success' : 'warning'}>
                    {m.approvedAt ? 'Approved' : 'Pending'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Intended use</div>
                  <div className="text-sm">{m.intendedUse || <span className="text-muted-foreground italic">—</span>}</div>
                </div>
                {m.outOfScopeUses?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Out of scope</div>
                    <ul className="mt-0.5 list-disc pl-4 text-xs text-muted-foreground">
                      {m.outOfScopeUses.slice(0, 3).map((u, i) => <li key={i}>{u}</li>)}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">EU AI Act</div>
                    <Badge variant="secondary" className="font-normal">{EU_AI_ACT_BY_RISK[m.riskLevel]}</Badge>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Risk level</div>
                    <Badge
                      variant={
                        m.riskLevel === 'HIGH' || m.riskLevel === 'UNACCEPTABLE'
                          ? 'destructive'
                          : m.riskLevel === 'LIMITED'
                            ? 'warning'
                            : 'secondary'
                      }
                    >
                      {m.riskLevel}
                    </Badge>
                  </div>
                </div>
                {m.humanOversightNotes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Human oversight</div>
                    <div className="text-xs text-muted-foreground">{m.humanOversightNotes}</div>
                  </div>
                )}
                {m.approvedAt ? (
                  <div className="text-[10px] text-muted-foreground">
                    Approved {formatDate(m.approvedAt)}
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="w-full" onClick={() => approve.mutate(m.id)}>
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve for use
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {createOpen && <CreateModelCardDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

/**
 * Guided 4-step wizard for creating a model card.
 *
 * Old single-pane form was sending fields the backend's Zod schema didn't
 * accept (purpose, euAiActClass, etc.) -- those got silently stripped. This
 * wizard maps 1:1 to ModelCardCreateSchema and walks non-governance staff
 * through each EU AI Act Article 11 / Annex IV section.
 */
type WizardStep = 'identity' | 'use' | 'data' | 'oversight' | 'review';

const WIZARD_STEPS: Array<{ id: WizardStep; title: string; subtitle: string }> = [
  { id: 'identity', title: 'Identity', subtitle: 'Model, provider, version, risk class' },
  { id: 'use', title: 'Intended use', subtitle: 'What it does and what it must not do' },
  { id: 'data', title: 'Data + evaluation', subtitle: 'Training data, eval, bias' },
  { id: 'oversight', title: 'Oversight', subtitle: 'Human-in-the-loop and limitations' },
  { id: 'review', title: 'Review', subtitle: 'Confirm and submit' },
];

interface WizardForm {
  modelName: string;
  provider: string;
  version: string;
  riskLevel: ModelCard['riskLevel'];
  intendedUse: string;
  outOfScopeUses: string[];
  trainingData: string;
  evaluationData: string;
  biasAssessment: string;
  metrics: Array<{ key: string; value: string }>;
  humanOversightNotes: string;
  limitations: string;
}

function CreateModelCardDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<WizardStep>('identity');
  const [form, setForm] = useState<WizardForm>({
    modelName: '',
    provider: '',
    version: '',
    riskLevel: 'HIGH',
    intendedUse: '',
    outOfScopeUses: [''],
    trainingData: '',
    evaluationData: '',
    biasAssessment: '',
    metrics: [{ key: '', value: '' }],
    humanOversightNotes: '',
    limitations: '',
  });

  const stepIdx = WIZARD_STEPS.findIndex((s) => s.id === step);

  const update = <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => setForm((prev) => ({ ...prev, [k]: v }));

  // Per-step validation -- gates the Continue button.
  const stepValid = (() => {
    switch (step) {
      case 'identity':
        return form.modelName.trim().length > 0 && form.provider.trim().length > 0 && form.version.trim().length > 0;
      case 'use':
        return form.intendedUse.trim().length >= 20;
      case 'data':
        return true; // optional fields
      case 'oversight':
        // High-risk cards REQUIRE oversight notes -- this is the high-risk-signoff guardrail.
        return form.riskLevel === 'MINIMAL' || form.riskLevel === 'LIMITED' || form.humanOversightNotes.trim().length > 0;
      case 'review':
        return true;
    }
  })();

  function back() {
    const i = stepIdx;
    if (i > 0) setStep(WIZARD_STEPS[i - 1].id);
  }

  function next() {
    const i = stepIdx;
    if (i < WIZARD_STEPS.length - 1) setStep(WIZARD_STEPS[i + 1].id);
  }

  const create = useMutation({
    mutationFn: async () => {
      const metrics: Record<string, string> = {};
      for (const { key, value } of form.metrics) {
        const k = key.trim();
        if (k) metrics[k] = value.trim();
      }
      const payload = {
        modelName: form.modelName.trim(),
        provider: form.provider.trim(),
        version: form.version.trim(),
        riskLevel: form.riskLevel,
        intendedUse: form.intendedUse.trim(),
        outOfScopeUses: form.outOfScopeUses.map((s) => s.trim()).filter((s) => s.length > 0),
        trainingData: form.trainingData.trim() || undefined,
        evaluationData: form.evaluationData.trim() || undefined,
        biasAssessment: form.biasAssessment.trim() || undefined,
        metrics,
        humanOversightNotes: form.humanOversightNotes.trim() || undefined,
        limitations: form.limitations.trim() || undefined,
      };
      return (await api.post('/api/ai-governance/model-cards', payload)).data;
    },
    onSuccess: () => {
      toast.success('Model card created', form.riskLevel === 'HIGH' || form.riskLevel === 'UNACCEPTABLE'
        ? 'High-risk card requires a second-reviewer Approve before it lands in any export.'
        : 'Card is now visible to reviewers.');
      qc.invalidateQueries({ queryKey: ['ai-gov', 'model-cards'] });
      onClose();
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  function submit() {
    create.mutate();
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New model card</DialogTitle>
          <DialogDescription>
            EU AI Act Article 11 + Annex IV documentation for one (model, version) pair. The wizard walks every
            required field with examples; the final card is what lands in your Compliance Pack.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <ol className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 p-2 text-xs">
          {WIZARD_STEPS.map((s, i) => {
            const active = s.id === step;
            const done = i < stepIdx;
            return (
              <li key={s.id} className="flex flex-1 items-center gap-2">
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                    done
                      ? 'bg-emerald-500 text-white'
                      : active
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted-foreground/20 text-muted-foreground',
                  )}
                >
                  {done ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                </span>
                <span className={cn('truncate text-[11px]', active && 'font-medium text-foreground')}>{s.title}</span>
              </li>
            );
          })}
        </ol>

        <div className="min-h-[260px]">
          {step === 'identity' && <IdentityStep form={form} update={update} />}
          {step === 'use' && <UseStep form={form} update={update} />}
          {step === 'data' && <DataStep form={form} update={update} />}
          {step === 'oversight' && <OversightStep form={form} update={update} />}
          {step === 'review' && <ReviewStep form={form} />}
        </div>

        <DialogFooter className="flex-row justify-between">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            {stepIdx > 0 && (
              <Button variant="outline" type="button" onClick={back}>
                Back
              </Button>
            )}
            {step !== 'review' ? (
              <Button type="button" onClick={next} disabled={!stepValid}>
                Continue
              </Button>
            ) : (
              <Button type="button" onClick={submit} disabled={create.isPending}>
                {create.isPending ? 'Saving...' : 'Create card'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Per-step components ---------------------------------------------------------

function IdentityStep({ form, update }: { form: WizardForm; update: <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => void }) {
  return (
    <div className="space-y-4 py-2">
      <p className="text-xs text-muted-foreground">
        Each model card pins one (model, version) pair. If you upgrade the model later, create a new card --
        don't edit this one. EU AI Act Article 11 expects model versioning to be auditable.
      </p>
      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <Label>Model name</Label>
          <Input value={form.modelName} onChange={(e) => update('modelName', e.target.value)} placeholder="claude-sonnet-4-5" required />
        </div>
        <div>
          <Label>Provider</Label>
          <Input value={form.provider} onChange={(e) => update('provider', e.target.value)} placeholder="Anthropic / OpenAI / internal" required />
        </div>
        <div>
          <Label>Version</Label>
          <Input value={form.version} onChange={(e) => update('version', e.target.value)} placeholder="2025-09-01" required />
        </div>
      </div>
      <div>
        <Label>EU AI Act risk class</Label>
        <select
          value={form.riskLevel}
          onChange={(e) => update('riskLevel', e.target.value as ModelCard['riskLevel'])}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="MINIMAL">Minimal -- transparency obligations only</option>
          <option value="LIMITED">Limited -- transparency + user disclosure</option>
          <option value="HIGH">High -- Annex III obligations (most pharma AI)</option>
          <option value="UNACCEPTABLE">Unacceptable -- prohibited per Article 5</option>
        </select>
        <p className="mt-1 text-xs text-muted-foreground">
          Pharma regulatory + PV use cases almost always sit in <strong>High</strong> per Annex III(8) (administration of justice and democratic processes -- "or" health). When in doubt, choose High.
        </p>
      </div>
    </div>
  );
}

function UseStep({ form, update }: { form: WizardForm; update: <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => void }) {
  const updateOos = (i: number, v: string) =>
    update('outOfScopeUses', form.outOfScopeUses.map((u, idx) => (idx === i ? v : u)));
  return (
    <div className="space-y-4 py-2">
      <div>
        <Label>Intended use (min 20 chars)</Label>
        <Textarea
          rows={3}
          value={form.intendedUse}
          onChange={(e) => update('intendedUse', e.target.value)}
          placeholder="Drafts ICSR safety narratives from raw case data for human review. Never closes cases. Never submits to regulators."
        />
        <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
          <span>Be specific. This text is quoted verbatim in the Compliance Pack.</span>
          <span className={form.intendedUse.length >= 20 ? 'text-emerald-600' : ''}>{form.intendedUse.length} / 20</span>
        </div>
      </div>
      <div>
        <Label>Out-of-scope uses</Label>
        <div className="space-y-2">
          {form.outOfScopeUses.map((u, i) => (
            <div key={i} className="flex gap-2">
              <Input value={u} onChange={(e) => updateOos(i, e.target.value)} placeholder="e.g. autonomous case closure" />
              {form.outOfScopeUses.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => update('outOfScopeUses', form.outOfScopeUses.filter((_, idx) => idx !== i))}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => update('outOfScopeUses', [...form.outOfScopeUses, ''])}
          >
            <Plus className="mr-1 h-3 w-3" /> Add another
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Listing what the model must NOT do is a regulator-favourite control. Pin the obvious ones: regulatory
          submission, case closure, substituting clinical judgment.
        </p>
      </div>
    </div>
  );
}

function DataStep({ form, update }: { form: WizardForm; update: <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => void }) {
  const updateMetric = (i: number, patch: Partial<{ key: string; value: string }>) =>
    update('metrics', form.metrics.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  return (
    <div className="space-y-4 py-2">
      <div>
        <Label>Training data summary</Label>
        <Textarea rows={2} value={form.trainingData} onChange={(e) => update('trainingData', e.target.value)} placeholder="e.g. Foundation model -- training data per vendor model card. No customer fine-tuning on tenant data." />
      </div>
      <div>
        <Label>Evaluation summary</Label>
        <Textarea rows={2} value={form.evaluationData} onChange={(e) => update('evaluationData', e.target.value)} placeholder="e.g. Internal 50-case golden set on EPAR overview extraction; 96% field-level accuracy at ship." />
      </div>
      <div>
        <Label>Bias assessment (optional)</Label>
        <Textarea rows={2} value={form.biasAssessment} onChange={(e) => update('biasAssessment', e.target.value)} placeholder="e.g. Tested for sex/age bias in PV narratives -- no statistically significant difference detected; methodology in QA-EVAL-2026-04." />
      </div>
      <div>
        <Label>Metrics (optional, key/value)</Label>
        <div className="space-y-2">
          {form.metrics.map((m, i) => (
            <div key={i} className="flex gap-2">
              <Input value={m.key} onChange={(e) => updateMetric(i, { key: e.target.value })} placeholder="metric name" />
              <Input value={m.value} onChange={(e) => updateMetric(i, { value: e.target.value })} placeholder="value" />
              {form.metrics.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => update('metrics', form.metrics.filter((_, idx) => idx !== i))}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => update('metrics', [...form.metrics, { key: '', value: '' }])}
          >
            <Plus className="mr-1 h-3 w-3" /> Add metric
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">e.g. accuracy = 0.96, F1 = 0.92, hallucination_rate = 0.4%.</p>
      </div>
    </div>
  );
}

function OversightStep({ form, update }: { form: WizardForm; update: <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => void }) {
  const required = form.riskLevel === 'HIGH' || form.riskLevel === 'UNACCEPTABLE';
  return (
    <div className="space-y-4 py-2">
      <div>
        <Label>
          Human oversight notes {required && <span className="text-destructive">*</span>}
        </Label>
        <Textarea
          rows={3}
          value={form.humanOversightNotes}
          onChange={(e) => update('humanOversightNotes', e.target.value)}
          placeholder="e.g. Every AI-generated narrative requires a qualified PV physician's e-signature before any case status change. Reviewers see the source page citation alongside each extraction."
        />
        {required && (
          <p className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            High-risk model cards must describe the human-in-the-loop control before they can be created.
          </p>
        )}
      </div>
      <div>
        <Label>Known limitations</Label>
        <Textarea rows={2} value={form.limitations} onChange={(e) => update('limitations', e.target.value)} placeholder="e.g. May hallucinate references; never to be cited as primary literature. Limited testing on languages other than English." />
      </div>
    </div>
  );
}

function ReviewStep({ form }: { form: WizardForm }) {
  return (
    <div className="space-y-3 py-2 text-sm">
      <p className="text-xs text-muted-foreground">
        Confirm and submit. The card lands in <code className="rounded bg-muted px-1">/admin/ai-governance</code>;
        high-risk cards require a separate ADMIN to click <strong>Approve</strong> before the next Compliance Pack
        export can include them.
      </p>
      <ReviewRow k="Model" v={`${form.provider} / ${form.modelName} v${form.version}`} />
      <ReviewRow k="Risk class" v={form.riskLevel} />
      <ReviewRow k="Intended use" v={form.intendedUse} />
      <ReviewRow k="Out-of-scope" v={form.outOfScopeUses.filter((u) => u.trim()).join(' / ') || '-'} />
      <ReviewRow k="Training data" v={form.trainingData || '-'} />
      <ReviewRow k="Evaluation" v={form.evaluationData || '-'} />
      <ReviewRow k="Bias" v={form.biasAssessment || '-'} />
      <ReviewRow
        k="Metrics"
        v={form.metrics.filter((m) => m.key.trim()).map((m) => `${m.key} = ${m.value}`).join(', ') || '-'}
      />
      <ReviewRow k="Human oversight" v={form.humanOversightNotes || '-'} />
      <ReviewRow k="Limitations" v={form.limitations || '-'} />
    </div>
  );
}

function ReviewRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b py-1.5 last:border-0">
      <div className="text-xs font-medium text-muted-foreground">{k}</div>
      <div className="text-xs">{v}</div>
    </div>
  );
}

function RiskRegisterSection() {
  const risks = useQuery({
    queryKey: ['ai-gov', 'risks'],
    queryFn: async () => (await api.get('/api/ai-governance/risks')).data,
  });

  return (
    <div className="space-y-4">
      {risks.isLoading ? (
        <Skeleton className="h-64" />
      ) : !risks.data?.items?.length ? (
        <EmptyState
          icon={Shield}
          title="Risk register is empty"
          description="Identify, assess and track residual risks for each high-risk AI use case."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {(risks.data.items as Risk[]).map((r) => {
                const tone =
                  r.residualRisk === 'VERY_HIGH' || r.residualRisk === 'HIGH'
                    ? 'text-destructive'
                    : r.residualRisk === 'MEDIUM'
                      ? 'text-amber-600'
                      : 'text-muted-foreground';
                return (
                  <div key={r.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={cn('h-4 w-4', tone)} />
                          <Badge variant="secondary">{r.area}</Badge>
                        </div>
                        <p className="mt-1 text-sm">{r.description}</p>
                        {r.mitigations && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            <strong className="text-foreground">Mitigation:</strong> {r.mitigations}
                          </p>
                        )}
                        <div className="mt-1 flex gap-3 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <span>Likelihood {r.likelihood.replace('_', ' ').toLowerCase()}</span>
                          <span>Impact {r.impact.replace('_', ' ').toLowerCase()}</span>
                          {r.owner && <span>Owner {r.owner}</span>}
                        </div>
                      </div>
                      <div className="text-right text-xs">
                        <div className={cn('font-display text-base font-semibold', tone)}>
                          {r.residualRisk.replace('_', ' ')}
                        </div>
                        <div className="text-muted-foreground">residual</div>
                        <Badge variant={r.status === 'CLOSED' ? 'success' : 'warning'} className="mt-1">
                          {r.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
